/**
 * Pure parser for slash-command input.
 *
 * No regex literals: the parser walks the string with ``trimStart`` /
 * ``indexOf`` so SonarQube's regex rules don't apply and the logic is
 * trivially verifiable by reading.
 */
import type { ParsedSlash } from "./types";

const SLASH_SIGIL = "/";
const ASCII_LETTER_LOWER_MIN = 97; // "a"
const ASCII_LETTER_LOWER_MAX = 122; // "z"
const ASCII_LETTER_UPPER_MIN = 65; // "A"
const ASCII_LETTER_UPPER_MAX = 90; // "Z"

/**
 * Split chat input into a slash token + the rest as args.
 *
 * Recognised shapes (token returned with leading slash):
 *   ``/compose``                       → { token: "/compose", args: "" }
 *   ``/compose to Ruban about X``      → { token: "/compose", args: "to Ruban about X" }
 *   ``  /reply   make it formal``      → { token: "/reply",   args: "make it formal" }
 *
 * Not a slash command (token = null, args = original):
 *   ``hello /compose world``           → leading text isn't a slash
 *   ``/``                              → empty token
 *   ``/123 do stuff``                  → digits aren't a valid command name
 *   ``""``                             → empty input
 *
 * The "letter-only command name" rule is what stops legitimate text like
 * ``"/etc/passwd"`` or ``"/api endpoint"`` from being matched against the
 * registry — those aren't slash commands.
 */
export function parseSlash(input: string): ParsedSlash {
  const trimmed = input.trimStart();
  if (!trimmed.startsWith(SLASH_SIGIL)) {
    return { token: null, args: input };
  }

  // Find the first whitespace position after the slash.
  const afterSlash = trimmed.slice(1);
  const spaceIdx = findFirstWhitespace(afterSlash);
  const nameRaw = spaceIdx < 0 ? afterSlash : afterSlash.slice(0, spaceIdx);

  // Only accept command names made of letters — keeps file-path / URL fragments
  // from being misinterpreted as slash commands.
  if (nameRaw.length === 0 || !isAllAsciiLetters(nameRaw)) {
    return { token: null, args: input };
  }

  const args = spaceIdx < 0 ? "" : afterSlash.slice(spaceIdx + 1).trim();
  return { token: `${SLASH_SIGIL}${nameRaw.toLowerCase()}`, args };
}

function findFirstWhitespace(s: string): number {
  for (let i = 0; i < s.length; i++) {
    const ch = s.codePointAt(i);
    // Space, tab, newline, carriage return — the only whitespace shapes the
    // textarea can produce.
    if (ch === 32 || ch === 9 || ch === 10 || ch === 13) {
      return i;
    }
  }
  return -1;
}

function isAllAsciiLetters(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const ch = s.codePointAt(i);
    if (ch === undefined) return false;
    const isLower = ch >= ASCII_LETTER_LOWER_MIN && ch <= ASCII_LETTER_LOWER_MAX;
    const isUpper = ch >= ASCII_LETTER_UPPER_MIN && ch <= ASCII_LETTER_UPPER_MAX;
    if (!isLower && !isUpper) return false;
  }
  return true;
}
